import React, { useState, useEffect, useRef } from "react";
import { FiMaximize, FiMinimize, FiDownload } from "react-icons/fi";
import GeneralData from "./OutboundGeneralData";
import { parseISO, sub, format } from "date-fns";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

import {
  EuiButton,
  EuiButtonEmpty,
  EuiDatePicker,
  EuiDatePickerRange,
  EuiLoadingSpinner,
  EuiPopover,
  EuiPopoverFooter,
  EuiPopoverTitle,
  EuiSelectable,
  EuiSuperDatePicker,
  EuiThemeProvider,
} from "@elastic/eui";
import { ThemeProvider, css } from "@emotion/react";
import { useTheme } from "../../../context/ThemeContext";
import DistributionByAgent from "./charts/DistributionByAgent";
import TimeAveragesPerAgent from "./charts/TimeAveragesPerAgent";
import TimelineDistribution from "./charts/TimelineDistribution";
import TimelineAverages from "./charts/TimelineAverages";
import AgentDistributionByQueue from "./charts/AgentDistributionByQueue";
import AgentDistributionByAttempts from "./charts/AgentDistributionByAttempts";
import PermissionsKo from "../../shared/PermissionsKo";

const Panel = ({
  title,
  children,
  hasControls = true,
  showTimeInterval = false,
  timeInterval,
  setTimeInterval,
  startDate,
  endDate,
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const panelRef = useRef(null);
  const { isDarkMode } = useTheme(); // Saber si estamos en modo oscuro

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    document.body.style.overflow = isFullScreen ? "auto" : "hidden";
  };

  const exportToImage = async () => {
    if (panelRef.current) {
      setIsExporting(true); // Mostrar loading
      try {
        // Ajustar escala según el estado de pantalla completa
        const scale = isFullScreen ? 3 : 2;

        // Capturar el contenido del panel con html2canvas
        const canvas = await html2canvas(panelRef.current, {
          useCORS: true,
          backgroundColor: isDarkMode ? "#1A202C" : "white", // Fondo adaptado al modo oscuro
          scale, // Escala ajustada para mejor calidad
        });

        // Crear un título y subtítulo (rango de fechas) en un canvas adicional
        const titleCanvas = document.createElement("canvas");
        const ctx = titleCanvas.getContext("2d");
        titleCanvas.width = canvas.width;
        titleCanvas.height = 120; // Espacio para título y rango

        // Fondo del título
        ctx.fillStyle = isDarkMode ? "#1A202C" : "white";
        ctx.fillRect(0, 0, titleCanvas.width, titleCanvas.height);

        // Título principal
        ctx.fillStyle = isDarkMode ? "white" : "black";
        ctx.font = "bold 24px Arial";
        ctx.textAlign = "center";
        ctx.fillText(title || "Panel", titleCanvas.width / 2, 40);

        // Subtítulo con rango de fechas
        ctx.font = "italic 16px Arial"; // Fuente más pequeña y estilizada
        ctx.fillStyle = "gray"; // Color más suave para el rango de fechas
        const dateRange = `Date Range: ${startDate} - ${endDate}`;
        ctx.fillText(dateRange, titleCanvas.width / 2, 80);

        // Combinar el título y el contenido del panel
        const combinedCanvas = document.createElement("canvas");
        combinedCanvas.width = canvas.width;
        combinedCanvas.height = canvas.height + titleCanvas.height;

        const combinedCtx = combinedCanvas.getContext("2d");
        combinedCtx.drawImage(titleCanvas, 0, 0); // Dibujar el título
        combinedCtx.drawImage(canvas, 0, titleCanvas.height); // Dibujar el contenido del panel

        // Convertir a imagen PNG
        const imgData = combinedCanvas.toDataURL("image/png");

        // Crear un enlace para descargar la imagen
        const link = document.createElement("a");
        link.href = imgData;
        link.download = `${
          title?.replace(/\s+/g, "_") || "Panel"
        }_${startDate}_to_${endDate}.png`;
        link.click();
      } catch (error) {
        console.error("Error exporting to image:", error);
      } finally {
        setIsExporting(false); // Ocultar loading
      }
    }
  };

  const handleTimeIntervalChange = (interval) => {
    setTimeInterval(interval);
  };

  return (
    <div
      className={`${
        isDarkMode
          ? "bg-[#1A202C] text-white"
          : "bg-primary-light text-text-primary-light"
      } dark:bg-[#1A202C] rounded-lg shadow-md p-2 flex flex-col transition-all duration-300 ${
        isFullScreen
          ? "absolute top-0 left-0 w-screen h-screen z-50 p-4"
          : "relative flex-grow min-h-[200px] max-h-[90vh] w-full overflow-visible"
      }`}
    >
      {/* Cabecera del Panel */}
      <div className="flex justify-between items-start">
        <h3 className="text-sm font-semibold">{title}</h3>
        <div className="flex items-center space-x-2">
          {showTimeInterval && (
            <div className="flex items-center space-x-2">
              {["1h", "1d", "1w", "1M"].map((interval) => (
                <button
                  key={interval}
                  className={`px-3 py-1 text-sm rounded-md ${
                    timeInterval === interval
                      ? "bg-blue-500 text-white"
                      : `${
                          isDarkMode
                            ? "hover:bg-blue-500 hover:text-white"
                            : "hover:bg-white hover:text-black"
                        } bg-secondary-light dark:bg-secondary-dark`
                  }`}
                  onClick={() => handleTimeIntervalChange(interval)}
                >
                  {interval}
                </button>
              ))}
            </div>
          )}
          {hasControls && (
            <div className="flex space-x-2">
              <button
                onClick={toggleFullScreen}
                className={`p-2 rounded-md hover:bg-blue-500 hover:text-white bg-secondary-light dark:bg-secondary-dark transition`}
                aria-label="Fullscreen"
              >
                {isFullScreen ? (
                  <FiMinimize size={14} />
                ) : (
                  <FiMaximize size={14} />
                )}
              </button>
              <button
                onClick={exportToImage}
                className={`p-2 rounded-md hover:bg-blue-500 hover:text-white bg-secondary-light dark:bg-secondary-dark transition`}
                aria-label="Exportar to PNG"
                disabled={isExporting}
              >
                {isExporting ? (
                  <EuiLoadingSpinner size="s" /> // Spinner visible durante la exportación
                ) : (
                  <FiDownload size={14} />
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contenido del Panel */}
      <div
        ref={panelRef}
        className="flex-1 flex items-center justify-center overflow-auto mt-1"
      >
        {React.cloneElement(children, { timeInterval })}{" "}
        {/* Pasa el intervalo */}
      </div>
    </div>
  );
};

const OutboundMain = () => {
  const [filters, setFilters] = useState({
    start: "now-24h",
    end: "now",
    queues: [],
  });
  const [timeIntervalDistribution, setTimeIntervalDistribution] =
    useState("1h");
  const [timeIntervalAverages, setTimeIntervalAverages] = useState("1h");

  const [appliedFilters, setAppliedFilters] = useState(filters); // Filtros aplicados

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [buttonState, setButtonState] = useState("Refresh");
  const [loading, setIsLoading] = useState(true);
  const [data, setData] = useState();
  const { isDarkMode } = useTheme();

  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const [options, setOptions] = useState([
    {
      label: "Error loading agents. Please contact support@astroline.com",
      key: "error",
      group: "agents",
      disabled: true,
    },
  ]);

  const handleQueueChange = (newOptions) => {
    const allQueuesSelected = newOptions.find(
      (opt) => opt.key === "allqueues" && opt.checked === "on"
    );

    const anyQueueSelected = newOptions.some(
      (opt) => opt.group === "queues" && opt.checked === "on"
    );

    const updatedOptions = newOptions.map((opt) => {
      if (opt.key === "allqueues") {
        // Seleccionar "All Queues" y deseleccionar todas las colas
        return { ...opt, checked: anyQueueSelected ? undefined : "on" };
      }

      if (opt.group === "queues") {
        // Si cualquier cola está seleccionada, deseleccionar "All Queues"
        return { ...opt, checked: allQueuesSelected ? undefined : opt.checked };
      }

      return opt;
    });

    setOptions(updatedOptions);
  };

  const handleTimeChange = ({ start, end }) => {
    setFilters((prev) => ({ ...prev, start, end }));
    setButtonState("Update");
  };

  const handleButtonClick = () => {
    const resolvedStart = resolveDate(filters.start);
    const resolvedEnd = resolveDate(filters.end);

    const differenceInDays =
      (resolvedEnd - resolvedStart) / (1000 * 60 * 60 * 24);
    console.log("La diferencia en días es", differenceInDays);

    let newTimeIntervalDistribution = "1h"; // Default
    let newTimeIntervalAverages = "1h"; // Default

    if (differenceInDays > 90) {
      newTimeIntervalDistribution = "1M"; // Mes
      newTimeIntervalAverages = "1M";
    } else if (differenceInDays > 30) {
      newTimeIntervalDistribution = "1w"; // Semana
      newTimeIntervalAverages = "1w";
    } else if (differenceInDays > 6) {
      newTimeIntervalDistribution = "1d"; // Día
      newTimeIntervalAverages = "1d";
    }

    // Aplica los filtros con los nuevos intervalos de tiempo
    setAppliedFilters({ ...filters });
    setTimeIntervalDistribution(newTimeIntervalDistribution);
    setTimeIntervalAverages(newTimeIntervalAverages);

    setButtonState("Refresh");
  };

  useEffect(() => {
    const themeLight = "/white.css";
    const themeDark = "/dark.css";

    const loadTheme = (themePath) => {
      const head = document.head;
      const link = document.createElement("link");

      link.type = "text/css";
      link.rel = "stylesheet";
      link.href = themePath;

      const existingLink = document.getElementById("theme-style");
      if (existingLink) {
        head.removeChild(existingLink);
      }

      link.id = "theme-style";
      head.appendChild(link);
    };

    if (isDarkMode) {
      loadTheme(themeDark);
    } else {
      loadTheme(themeLight);
    }

    return () => {
      const existingLink = document.getElementById("theme-style");
      if (existingLink) {
        document.head.removeChild(existingLink);
      }
    };
  }, [isDarkMode]);

  const resolveDate = (dateStr) => {
    const now = new Date();

    // Helper para formatear fecha en español
    const formatDateToSpanish = (date) => {
      return new Intl.DateTimeFormat("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    };

    if (dateStr.startsWith("now")) {
      const offset = dateStr.match(/now-(\d+)([hdwMy])/); // Regex para extraer la unidad y el valor
      if (offset) {
        const [, value, unit] = offset;
        const amount = parseInt(value, 10);
        let resolvedDate;
        switch (unit) {
          case "h":
            resolvedDate = sub(now, { hours: amount });
            break;
          case "d":
            resolvedDate = sub(now, { days: amount });
            break;
          case "w":
            resolvedDate = sub(now, { weeks: amount });
            break;
          case "M":
            resolvedDate = sub(now, { months: amount });
            break;
          case "y":
            resolvedDate = sub(now, { years: amount });
            break;
          default:
            resolvedDate = now;
        }
        return formatDateToSpanish(resolvedDate);
      }
      return formatDateToSpanish(now); // Si es "now" sin offset
    }

    // Si es una fecha absoluta, la parseamos y la formateamos
    return formatDateToSpanish(parseISO(dateStr));
  };

  return (
    <div className="h-[95vh] flex flex-col gap-4 p-4">
      {isUnauthorized ? (
        <PermissionsKo />
      ) : (
        <>
          <div className="h-[5vh] flex items-center justify-between py-2">
            <EuiThemeProvider colorMode={isDarkMode ? "dark" : "light"}>
              <div className="flex items-center">
                <EuiPopover
                  panelPaddingSize="none"
                  button={
                    <EuiButton
                      iconType="arrowDown"
                      iconSide="right"
                      onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                      className="text-sm font-semibold"
                    >
                      Filter Agents
                    </EuiButton>
                  }
                  isOpen={isPopoverOpen}
                  closePopover={() => setIsPopoverOpen(false)}
                >
                  <EuiSelectable
                    aria-label="Selectable + popover example"
                    searchable
                    searchProps={{
                      placeholder: "Start typing...",
                      compressed: true,
                    }}
                    options={options}
                    onChange={handleQueueChange}
                  >
                    {(list, search) => (
                      <div
                        style={{
                          width: 240,
                          color: isDarkMode ? "#f7fafc" : "#2d3748", // Texto blanco si es dark
                        }}
                      >
                        <EuiPopoverTitle
                          paddingSize="s"
                          style={{ color: isDarkMode ? "#f7fafc" : "#2d3748" }} // También ajusta el título
                        >
                          {search}
                        </EuiPopoverTitle>
                        {list}
                      </div>
                    )}
                  </EuiSelectable>
                </EuiPopover>
              </div>

              <div className="flex items-center space-x-2">
                <EuiSuperDatePicker
                  start={filters.start}
                  end={filters.end}
                  onTimeChange={handleTimeChange}
                  showUpdateButton={false}
                />

                <EuiButton
                  onClick={handleButtonClick}
                  fill={buttonState === "Update"}
                >
                  {buttonState}
                </EuiButton>
              </div>
            </EuiThemeProvider>
          </div>
          <GeneralData
            filters={appliedFilters}
            data={data}
            loading={loading}
            error={false}
            setIsUnauthorized={setIsUnauthorized}
          />
          <div className="flex-1 grid grid-rows-3 gap-4 overflow-y-auto">
            {/* Primera fila: Ocupa 2/3 del alto con dos columnas */}
            <div className="row-span-2 grid grid-cols-2 gap-4">
              <Panel
                title="Distribution by agent"
                startDate={resolveDate(appliedFilters?.start)}
                endDate={resolveDate(appliedFilters?.end)}
              >
                <DistributionByAgent filters={appliedFilters} />
              </Panel>
              <Panel
                title="Time averages per agent"
                startDate={resolveDate(appliedFilters?.start)}
                endDate={resolveDate(appliedFilters?.end)}
              >
                <TimeAveragesPerAgent filters={appliedFilters} />
              </Panel>
            </div>

            {/* Segunda fila: Ocupa 1/3 del alto con una única columna */}
            <div className="row-span-1 grid grid-cols-1">
              <Panel
                title="Timeline Distribution"
                showTimeInterval={true}
                timeInterval={timeIntervalDistribution}
                setTimeInterval={setTimeIntervalDistribution}
                startDate={resolveDate(appliedFilters?.start)}
                endDate={resolveDate(appliedFilters?.end)}
              >
                <TimelineDistribution
                  filters={appliedFilters}
                  timeInterval={timeIntervalDistribution}
                />
              </Panel>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OutboundMain;
